import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import {
  AuthContext,
  INITIAL_AUTH_STATE,
  type AuthState,
  type AuthUser,
} from './AuthContext.ts';
import { isSessionInvalid } from './errors.ts';
import { isNetworkError } from '../lib/networkError.ts';
import { recordClientFailure } from '../telemetry/rum.ts';
import { clearAllSessions } from '../agents/sessionStore.ts';

/**
 * Cognito groups from the ACCESS token payload — the token the agent APIs
 * authorize on (`getAccessToken` sends it as the bearer), so UI gating and
 * server enforcement read the same claim. An absent/malformed claim is
 * simply "no groups": this must never throw (P3) — most users carry only
 * the baseline `members` group or none at all.
 */
function groupsFromPayload(raw: unknown): readonly string[] {
  return Array.isArray(raw) ? raw.filter((g): g is string => typeof g === 'string') : [];
}

/**
 * A session restore that fails because the request never reached Cognito is
 * NOT "you are signed out" — it is "we could not tell". Retry a couple of
 * times before concluding anything, because the failure is transient by
 * definition and the cost of getting it wrong is bouncing a signed-in user to
 * the sign-in page over a momentary wifi drop.
 *
 * Kept small on purpose: this runs on every mount, so the worst case a genuinely
 * offline visitor pays before seeing the signed-out UI is
 * ATTEMPTS x DELAY = 600ms.
 */
const NETWORK_RETRY_ATTEMPTS = 2;
const NETWORK_RETRY_DELAY_MS = 300;

async function loadUser(): Promise<AuthUser | null> {
  // Throws when there's no signed-in user (Amplify uses several distinct
  // error shapes for that) OR when the cached refresh token is dead — the
  // caller (`refresh`) distinguishes the two via `isSessionInvalid`.
  const current = await getCurrentUser();
  const attrs = await fetchUserAttributes();
  if (!attrs.email) {
    // Pool enforces email at signup, but be defensive about the attribute
    // not having propagated yet.
    return null;
  }
  const session = await fetchAuthSession();
  return {
    sub: current.userId,
    email: attrs.email,
    givenName: attrs.given_name,
    familyName: attrs.family_name,
    preferredUsername: attrs.preferred_username,
    groups: groupsFromPayload(session.tokens?.accessToken.payload['cognito:groups']),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL_AUTH_STATE);
  // Guards against re-entrant purges: a single revoked-token event can
  // surface through both `loadUser()` and the `tokenRefresh_failure` Hub
  // event nearly simultaneously, and `amplifySignOut()` itself fires
  // `signedOut` — we only want one local sign-out in flight.
  const purgingRef = useRef(false);

  // Drop the cached (revoked/expired) tokens so the next sign-in starts
  // from a clean slate. Local-only on purpose: the refresh token is
  // already dead, so there's nothing to revoke server-side and a global
  // sign-out would just 400 on the same inactive token. `signOut` clears
  // local storage even when its network revoke call fails.
  const purgeDeadSession = useCallback(async () => {
    if (purgingRef.current) {
      return;
    }
    purgingRef.current = true;
    try {
      await amplifySignOut();
    } catch {
      // Local storage is cleared regardless; nothing actionable here.
    } finally {
      purgingRef.current = false;
    }
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    const signedOut = (): boolean => {
      setState((prev) => ({ ...prev, status: 'unauthenticated', user: null }));
      return false;
    };

    for (let attempt = 0; attempt <= NETWORK_RETRY_ATTEMPTS; attempt += 1) {
      try {
        const user = await loadUser();
        setState((prev) => ({
          ...prev,
          status: user ? 'authenticated' : 'unauthenticated',
          user,
        }));
        return user !== null;
      } catch (err) {
        // A revoked/expired refresh token (e.g. after a global sign-out)
        // makes Amplify replay a dead token on session restore and throw
        // `NotAuthorizedException: Token is inactive`. Purge it so we don't
        // loop on the same dead token every load and so the next sign-in is
        // unblocked. Definitive — never retried.
        if (isSessionInvalid(err)) {
          await purgeDeadSession();
          return signedOut();
        }

        // The request never reached Cognito. This is the case that used to be
        // silently reclassified as "not signed in": `loadUser()` makes three
        // network calls and `AuthProvider` calls `refresh()` on EVERY mount,
        // so a momentary drop signed the user out and bounced them to
        // /auth/signin with nothing recorded anywhere.
        if (isNetworkError(err)) {
          if (attempt < NETWORK_RETRY_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
            continue;
          }
          // Out of retries: still sign out (we genuinely cannot establish a
          // session) but make it findable. Reported ONCE per refresh, never
          // per attempt. PII-free: error name + message only.
          recordClientFailure(
            'auth_refresh_network_failure',
            err instanceof Error ? `${err.name}: ${err.message}` : String(err),
          );
          return signedOut();
        }

        // Anything else — the ordinary "no current user" shapes Amplify throws
        // for a signed-out visitor. Expected, not a failure: no retry, no
        // telemetry.
        return signedOut();
      }
    }
    // Unreachable: the loop either returns or continues. Satisfies the
    // compiler without inventing a fallback state.
    return signedOut();
  }, [purgeDeadSession]);

  const signOut = useCallback(async () => {
    await amplifySignOut({ global: true });
    // Hub will fire 'signedOut' and the listener below also clears state;
    // do it synchronously too so the caller can navigate immediately.
    setState((prev) => ({ ...prev, status: 'unauthenticated', user: null }));
  }, []);

  // Self-recover from a dead session surfaced by a request (e.g. the agent
  // endpoint 401s): purge the cached tokens locally and drop to
  // 'unauthenticated' so RequireAuth routes the user back to sign-in, instead
  // of leaving them stuck in a "signed in but every request 401s" limbo. Same
  // recovery as the `tokenRefresh_failure` Hub event.
  const expireSession = useCallback(() => {
    void (async () => {
      await purgeDeadSession();
      setState((prev) => ({ ...prev, status: 'unauthenticated', user: null }));
    })();
  }, [purgeDeadSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Hub events keep state in sync when sign-in/sign-out happens in a
    // sibling tab or via the SDK directly (not just via this provider).
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'signedIn' ||
        payload.event === 'signInWithRedirect'
      ) {
        // A fresh interactive sign-in starts every agent from a new session —
        // re-authenticating should never resume the prior login's conversation.
        // (A silent `tokenRefresh` below is NOT a new sign-in, so it doesn't.)
        clearAllSessions();
        void refresh();
      } else if (payload.event === 'tokenRefresh') {
        void refresh();
      } else if (payload.event === 'signedOut') {
        setState((prev) => ({ ...prev, status: 'unauthenticated', user: null }));
      } else if (payload.event === 'tokenRefresh_failure') {
        // A background token refresh failed mid-session — the refresh token
        // was revoked (global sign-out elsewhere) or expired. Same recovery
        // as an agent-surfaced 401.
        expireSession();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [refresh, expireSession]);

  return (
    <AuthContext.Provider value={{ ...state, refresh, signOut, expireSession }}>
      {children}
    </AuthContext.Provider>
  );
}
